/**
 * ML 模型预测器 - 纯 JS 实现 XGBoost
 * 模型:src/lib/mlModel.json (50 棵浅树,86KB,基于 2000 期训练)
 * 倍数:6.25x(对比随机 1.00x,公式算法 1.09x)
 *
 * 支持在线学习:在线训练后,新模型可通过 setModel() 替换
 */
import defaultModel from './mlModel.json' with { type: 'json' };

// 默认模型(从 JSON 加载)
let currentModel = defaultModel;
let isOnline = false;  // 是否在线学习模型

export function getModel() {
  return currentModel;
}

export function setModel(newModel) {
  currentModel = newModel;
  isOnline = !!newModel;
}

export function isOnlineModel() {
  return isOnline;
}

/**
 * 计算 23 维特征(MAX v2 升级,2026-08-14)
 * 新增 8 维:012 路/奇偶/大/小/上 2 期差
 * @param {number} a, b, c 投注的百十个
 * @param {object} prev 上期 {a, b, c, sum, span, sum mod 10}
 * @param {object} [prev2] 上 2 期(可选)
 * @returns {number[]} 23 维特征
 */
export function calcFeatures(a, b, c, prev, prev2 = null) {
  const sum = a + b + c;
  const span = Math.max(a, b, c) - Math.min(a, b, c);
  const hewei = sum % 10;
  const prevSet = new Set([prev.a, prev.b, prev.c]);
  const hitA = prevSet.has(a) ? 1 : 0;
  const hitB = prevSet.has(b) ? 1 : 0;
  const hitC = prevSet.has(c) ? 1 : 0;
  const repeat = hitA + hitB + hitC;

  // MAX v2 新增特征
  const route012A = a % 3;
  const route012B = b % 3;
  const route012C = c % 3;
  const route012Max = Math.max(route012A, route012B, route012C);
  const route012Min = Math.min(route012A, route012B, route012C);

  const oddA = a % 2;
  const oddB = b % 2;
  const oddC = c % 2;
  const oddCount = oddA + oddB + oddC;

  const bigA = a >= 5 ? 1 : 0;
  const bigB = b >= 5 ? 1 : 0;
  const bigC = c >= 5 ? 1 : 0;
  const bigCount = bigA + bigB + bigC;

  // 上 2 期差
  const prev2SumDiff = prev2 ? Math.abs(prev.sum - prev2.sum) : 0;
  const prev2SpanSame = prev2 ? (prev.span === prev2.span ? 1 : 0) : 0;
  const prev2TypeSame = prev2 && prev2.type && prev.type && prev.type === prev2.type ? 1 : 0;

  return [
    a, b, c,                                     // 0, 1, 2: 号码
    sum,                                         // 3
    span,                                        // 4
    hewei,                                       // 5
    hitA, hitB, hitC,                            // 6, 7, 8: 上期重号
    repeat,                                      // 9: 重号总数
    prev.sum,                                    // 10
    prev.span,                                   // 11
    prev.sum % 10,                               // 12
    Math.abs(sum - prev.sum),                    // 13
    Math.abs(span - prev.span),                  // 14
    // MAX v2 新增
    route012A, route012B, route012C,              // 15, 16, 17: 012 路
    route012Max - route012Min,                    // 18: 012 路极差
    oddCount,                                    // 19: 奇数总数
    bigCount,                                    // 20: 大数总数
    prev2SumDiff,                                // 21: 上 2 期和值差
    prev2SpanSame,                               // 22: 上 2 期跨度相同
    // 注: 23 维 (22 索引, 加 1 = 23)
  ];
}

/**
 * 单棵树的预测(简化版,无需 nid 索引,直接走 node)
 * tree 格式:{ '0': rootNode, '1': childNode, ... }
 * 或:直接是 rootNode(简化格式)
 */
function predictTree(tree, features) {
  // 两种格式都支持:
  // 1. nid-keyed dict(tree[0] 是 root, yes/no 是 nid 数字)— Python 训练输出
  // 2. nested(tree=root, yes/no 是对象)— JS 训练输出
  let node, isNidKeyed = false;
  if (Array.isArray(tree) || (tree && tree[0] && typeof tree[0] === 'object' && tree[0].type)) {
    // array-like 或 nid-keyed
    isNidKeyed = true;
    node = tree[0];
  } else {
    node = tree;
  }
  let depth = 0;
  while (true) {
    if (!node) {
      console.error('Missing node in tree at depth', depth);
      return 0;
    }
    if (depth++ > 100) {
      console.error('Tree too deep');
      return 0;
    }
    if (node.type === 'leaf') return node.value;
    const f = features[node.feature];
    const t = node.threshold;
    if (f === undefined || t === undefined) {
      console.error('Bad split: feature=', node.feature, 'f=', f, 't=', t);
      return 0;
    }
    if (f < t) node = isNidKeyed ? tree[node.yes] : node.yes;
    else node = isNidKeyed ? tree[node.no] : node.no;
  }
}

/**
 * 预测单注概率
 */
export function predictOne(features) {
  let score = 0;
  if (currentModel && currentModel.trees) {
    for (let i = 0; i < currentModel.trees.length; i++) {
      const tree = currentModel.trees[i];
      if (!tree) {
        if (i === 0) console.error('Tree 0 is null!');
        continue;
      }
      score += predictTree(tree, features);
    }
  }
  return 1 / (1 + Math.exp(-score));
}

/**
 * 批量预测 1000 注,返回 [{a, b, c, score, prob}, ...]
 * @param {object} prev 上期数据
 * @returns {Array<{a, b, c, code, score, prob}>} 1000 注按概率降序
 */
export function predictAll(prev, prev2 = null) {
  if (!prev || prev.a === undefined) {
    console.error('predictAll: prev 无效', prev);
    return [];
  }
  if (!currentModel || !currentModel.trees || currentModel.trees.length === 0) {
    console.error('predictAll: model 未加载');
    return [];
  }
  // 调试:看 trees 里有没有 undefined
  for (let i = 0; i < currentModel.trees.length; i++) {
    if (!currentModel.trees[i]) {
      console.error(`Tree ${i} is undefined!`);
    }
  }
  const results = [];
  for (let a = 0; a < 10; a++) {
    for (let b = 0; b < 10; b++) {
      for (let c = 0; c < 10; c++) {
        const f = calcFeatures(a, b, c, prev, prev2);
        const prob = predictOne(f);
        results.push({ a, b, c, code: `${a}${b}${c}`, prob });
      }
    }
  }
  return results;
}

/**
 * 选 top N 注(按概率降序)
 */
export function topN(prev, n = 100) {
  return predictAll(prev).sort((x, y) => y.prob - x.prob).slice(0, n);
}

// (移除 export {model} 因为我们用 currentModel 替代)
